import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight, Star, Zap, Shield, Users, TrendingUp } from 'lucide-react';
import { mockData } from '../mock/data';
import LocationsSection from '../components/sections/LocationsSection';
import { useTranslation } from '../hooks/useTranslation';
import { useSEO } from '../hooks/useSEO';

const PricingPage = () => {
  const { type } = useParams();
  const { pricing } = mockData;
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const { t, language } = useTranslation();
  useSEO('pricing');

  // If no type specified, show both
  const showBoth = !type;
  const showResidential = !type || type === 'residential';
  const showMobile = !type || type === 'mobile';

  const getTranslatedPlanName = (planName) => {
    const { language } = useTranslation();
    if (language === 'zh') {
      switch (planName) {
        case 'Starter': return '入门版';
        case 'Professional': return '专业版';
        case 'Business': return '商业版';
        case 'Scale': return '规模版';
        case 'Enterprise': return '企业版';
        case 'Trial': return '试用版';
        case 'Monthly': return '月付版';
        default: return planName;
      }
    }
    return planName;
  };

  const getTranslatedVolume = (volume) => {
    const { language } = useTranslation();
    if (language === 'zh') {
      switch (volume) {
        case '1GB - 30GB': return '1GB - 30GB';
        case '30GB - 100GB': return '30GB - 100GB';
        case '100GB - 1TB': return '100GB - 1TB';
        case '1TB - 5TB': return '1TB - 5TB';
        case '5TB+': return '5TB+';
        case '24 hours': return '24小时';
        case '30 days': return '30天';
        case 'Volume pricing': return '批量定价';
        default: return volume;
      }
    }
    return volume;
  };

  const getTranslatedUnit = (unit) => {
    const { language } = useTranslation();
    if (language === 'zh') {
      switch (unit) {
        case 'per GB': return '每GB';
        case 'per proxy/24h': return '每代理/24小时';
        case 'per proxy/month': return '每代理/月';
        case 'contact sales': return '联系销售';
        default: return unit;
      }
    }
    return unit;
  };

  const getTranslatedPrice = (price) => {
    const { language } = useTranslation();
    if (language === 'zh') {
      switch (price) {
        case 'Custom': return '定制';
        default: return price;
      }
    }
    return price;
  };

  const getTranslatedMobileTitle = () => {
    const { language } = useTranslation();
    if (language === 'zh') {
      return '无限移动代理';
    }
    return 'Unlimited Mobile Proxies';
  };

  const getTranslatedMobileSubtitle = () => {
    const { language } = useTranslation();
    if (language === 'zh') {
      return '真实移动IP与运营商轮换';
    }
    return 'True mobile IPs with carrier rotation';
  };

  const getTranslatedExample = (example) => {
    const { language } = useTranslation();
    if (language === 'zh') {
      switch (example) {
        case '1GB = $1.30': return '1GB = $1.30';
        case '50GB = $55': return '50GB = $55';
        case '500GB = $500': return '500GB = $500';
        case '2TB = $1,800': return '2TB = $1,800';
        default: return example;
      }
    }
    return example;
  };

  const getTranslatedFeatures = (plan, planType) => {
    const { language } = useTranslation();
    if (language === 'en') {
      return plan.features; // Return original English features
    }
    
    if (planType === 'residential') {
      switch (plan.name) {
        case 'Starter':
          return [
            "3亿+住宅IP",
            "国家定位", 
            "HTTP/HTTPS支持",
            "24/7支持",
            "99.9%正常运行时间"
          ];
        case 'Professional':
          return [
            "包含入门版所有功能",
            "城市定位",
            "ISP定位",
            "优先支持",
            "专用IP池",
            "API访问"
          ];
        case 'Business':
          return [
            "包含专业版所有功能",
            "定制集成",
            "客户经理",
            "SLA保证",
            "私有IP池",
            "高级分析"
          ];
        case 'Scale':
          return [
            "包含商业版所有功能",
            "优先路由",
            "定制端点",
            "白标选项",
            "专用基础设施",
            "24/7电话支持"
          ];
        case 'Enterprise':
          return [
            "包含规模版所有功能",
            "批量折扣",
            "定制合同",
            "本地部署选项",
            "合规支持",
            "培训与入职"
          ];
        default:
          return plan.features;
      }
    } else if (planType === 'mobile') {
      switch (plan.name) {
        case 'Trial':
          return [
            "真实移动IP",
            "运营商轮换",
            "4G/5G网络",
            "基础支持",
            "52+国家可用"
          ];
        case 'Monthly':
          return [
            "包含试用版所有功能",
            "延长使用时间",
            "优先支持",
            "定制轮换",
            "52+全球位置",
            "粘性会话"
          ];
        case 'Enterprise':
          return [
            "专用移动IP池",
            "SLA保证",
            "24/7支持",
            "定制解决方案",
            "私有APN",
            "批量折扣"
          ];
        default:
          return plan.features;
      }
    }
    return plan.features;
  };

  const PricingCard = ({ plan, index, isPopular, planType }) => {
    const translatedFeatures = getTranslatedFeatures(plan, planType);
    
    return (
      <div className={`relative ${isPopular ? 'scale-105 z-10' : ''}`}>
        {isPopular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
            <Badge className="bg-cyan-500 text-slate-950 px-3 py-1.5 text-xs font-semibold whitespace-nowrap">
              <Star className="w-3 h-3 mr-1" />
{t('pricingPage.mostPopular')}
            </Badge>
          </div>
        )}
        
        <div className={`h-full bg-slate-950/50 backdrop-blur-sm border rounded-xl p-6 transition-all duration-300 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/10 ${
          isPopular 
            ? 'border-cyan-500/50 ring-1 ring-cyan-500/25 shadow-xl shadow-cyan-500/20' 
            : 'border-slate-800'
        }`}>
          {/* Plan Header */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-white mb-1">{getTranslatedPlanName(plan.name)}</h3>
            <p className="text-slate-400 text-sm mb-4">{getTranslatedVolume(plan.volume)}</p>
            
            {/* Pricing Display */}
            <div className="mb-4">
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className={`text-2xl font-bold ${
                  planType === 'residential' ? 'text-cyan-400' : 'text-green-400'
                }`}>
                  {getTranslatedPrice(plan.price)}
                </span>
                <span className="text-slate-400 text-sm">{getTranslatedUnit(plan.unit)}</span>
              </div>
              
              {plan.monthlyFrom !== plan.price && (
                <div className="text-slate-500 text-xs">
                  {t('pricingPage.from')} {plan.monthlyFrom}/{t('pricingPage.month')}
                </div>
              )}
            </div>
            
            {plan.savings && language === 'en' && (
              <div className="inline-flex items-center bg-green-500/10 text-green-400 px-2 py-1 rounded-full text-xs font-medium">
                <TrendingUp className="w-3 h-3 mr-1" />
                {t('pricingPage.save')} {plan.savings}
              </div>
            )}
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            {translatedFeatures.map((feature, fIndex) => (
              <div key={fIndex} className="flex items-start space-x-2">
                <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  planType === 'residential' ? 'text-cyan-400' : 'text-green-400'
                }`} />
                <span className="text-slate-300 text-sm leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          {plan.price === 'Custom' ? (
            <a 
              href="mailto:sales@ip9.io"
              className="inline-block w-full"
            >
              <Button 
                className={`w-full ${
                  isPopular 
                    ? `${planType === 'residential' ? 'bg-cyan-500 hover:bg-cyan-400' : 'bg-green-400 hover:bg-green-300'} text-slate-950 font-semibold`
                    : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-700'
                }`}
              >
                {t('pricingPage.contactSales')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          ) : (
            <a 
              href="https://dashboard.ip9.io" 
              className="inline-block w-full"
            >
              <Button 
                className={`w-full ${
                  isPopular 
                    ? `${planType === 'residential' ? 'bg-cyan-500 hover:bg-cyan-400' : 'bg-green-400 hover:bg-green-300'} text-slate-950 font-semibold`
                    : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-700'
                }`}
              >
                {t('pricingPage.getStarted')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {type === 'residential' ? t('pricingPage.residentialProxy') : 
                 type === 'mobile' ? t('pricingPage.mobileProxy') : 
                 t('pricingPage.simpleTransparent')} 
                <span className="block text-cyan-500">
                  {t('pricingPage.pricing')}
                </span>
              </h1>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
                {type === 'residential' ? t('pricingPage.residentialSubtitle') :
                 type === 'mobile' ? t('pricingPage.mobileSubtitle') :
                 t('pricingPage.generalSubtitle')}
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-400">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span>{t('pricingPage.uptimeSLA')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  <span>{t('pricingPage.happyCustomers')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  <span>{t('pricingPage.instantActivation')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Residential Pricing */}
            {showResidential && (
              <div className="mb-20">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    {t('pricingPreview.residentialProxies')}
                  </h2>
                  <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                    {t('pricingPreview.residentialSubtitle')}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-4">
                  {pricing.residential.plans.map((plan, index) => (
                    <PricingCard
                      key={index}
                      plan={plan}
                      index={index}
                      isPopular={plan.popular}
                      planType="residential"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Pricing */}
            {showMobile && (
              <div>
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    {getTranslatedMobileTitle()}
                  </h2>
                  <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                    {getTranslatedMobileSubtitle()}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                  {pricing.mobile.plans.map((plan, index) => (
                    <PricingCard
                      key={index}
                      plan={plan}
                      index={index}
                      isPopular={plan.popular}
                      planType="mobile"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Features Comparison */}
        {showResidential && (
          <section className="py-16 bg-slate-950">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">
                  {t('pricingPage.pricingCalculator')}
                </h2>
                <p className="text-slate-400">
                  {t('pricingPage.calculatorSubtitle')}
                </p>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-3xl p-8 max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {pricing.residential.plans.slice(0, 4).map((plan, index) => (
                    <div key={index} className="text-center p-6 bg-slate-800/30 rounded-2xl border border-slate-700">
                      <div className="text-2xl font-bold text-cyan-400 mb-2">{getTranslatedPrice(plan.price)}</div>
                      <div className="text-slate-400 text-sm mb-3">{getTranslatedUnit(plan.unit)}</div>
                      <div className="text-white font-medium mb-2">{getTranslatedVolume(plan.volume)}</div>
                      <div className="text-slate-500 text-sm">
                        {plan.name === 'Starter' && getTranslatedExample('1GB = $1.30')}
                        {plan.name === 'Professional' && getTranslatedExample('50GB = $55')}
                        {plan.name === 'Business' && getTranslatedExample('500GB = $500')}
                        {plan.name === 'Scale' && getTranslatedExample('2TB = $1,800')}
                      </div>
                      {plan.savings && language === 'en' && (
                        <div className="inline-flex items-center bg-green-500/10 text-green-400 px-2 py-1 rounded-full text-xs mt-2">
                          {t('pricingPage.save')} {plan.savings}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Locations Section - Show for mobile proxies */}
        {showMobile && (
          <LocationsSection />
        )}

        {/* FAQ Section */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                {t('pricingPage.faq.title')}
              </h2>
              <p className="text-slate-400">
                {t('pricingPage.faq.subtitle')}
              </p>
            </div>

            <div className="space-y-6">
              {t('pricingPage.faq.questions').map((faq, index) => (
                <div key={index} className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-3">{faq.question}</h3>
                  <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-3xl p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('pricingPage.cta.title')}
              </h2>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                {t('pricingPage.cta.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <a 
                  href="https://dashboard.ip9.io" 
                  className="inline-block"
                >
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-4 text-lg">
                    {t('pricingPage.cta.startPlan')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a 
                  href="mailto:sales@ip9.io"
                  className="inline-block"
                >
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-4 text-lg">
                    {t('pricingPage.cta.contactSales')}
                  </Button>
                </a>
              </div>
              
              <div className="flex items-center justify-center space-x-8 mt-8 text-sm text-slate-500">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>{t('pricingPage.cta.noSetupFees')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>{t('pricingPage.cta.cancelAnytime')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>{t('pricingPage.cta.support247')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PricingPage;